import { QuestionAnswerMap } from "@care-portals/api-interfaces";
import { calculateBMI, formatHeight } from "../../../utils/bmi";

const pcosFollowupQuestions = [
  {
    label: 'What was your sex assigned at birth?',
    field: 'pcosFollowup_sex',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'male', value: 'Male' },
        { key: 'female', value: 'Female' }
      ]
    },
    extras: { sendToBeluga: false }
  },
  {
    label: 'What is your:',
    field: 'pcosFollowup_weightHeightBMI',
    type: 'info',
    required: false,
    answer: {
      type: 'list',
      required: false,
      items: [
        {
          type: 'weightHeight',
          showLabel: true
        },
        {
          type: 'calculated',
          label: 'Your BMI is',
          answer: {
            formula: data => calculateBMI(data)
          }
        },
        {
          type: 'calculated',
          label: 'Height',
          answer: {
            formula: data => formatHeight(data)
          }
        },
        {
          type: 'calculated',
          label: 'Weight',
          answer: {
            formula: data => {
              const { weight, weightUnit } = data;
              return (weight || '') + ' ' + (weightUnit || '');
            }
          }
        }
      ]
    }
  },
  {
    label:
      'Have there been any health or medication changes, ED visits or hospitalizations since your last check-in?',
    field: 'pcosFollowup_healthChanges',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'no', value: 'No' },
        { key: 'yes', value: 'Yes' }
      ]
    }
  },
  {
    label: 'Please tell us more about the changes in your health',
    field: 'pcosFollowup_healthChangesDetails',
    type: 'info',
    answer: {
      name: 'pcosFollowup_healthChangesDetails',
      type: 'text',
      rows: 4,
      placeholder: 'Enter details'
    }
  },
  {
    label: 'How are you doing on the current medication?',
    field: 'pcosFollowup_currentMedicationStatus',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'well', value: 'Well' },
        { key: 'not_well', value: 'Not well' }
      ]
    }
  },
  {
    label: 'Please tell us more about what you are experiencing',
    field: 'pcosFollowup_currentMedicationStatusDetails',
    type: 'info',
    answer: {
      name: 'pcosFollowup_currentMedicationStatusDetails',
      type: 'text',
      rows: 4,
      placeholder: 'Enter details'
    }
  },
  {
    label:
      'Have you experienced any side effects from the current medication?',
    field: 'pcosFollowup_sideEffects',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'no', value: 'No' },
        { key: 'yes', value: 'Yes' }
      ]
    }
  },
  {
    label: 'Please tell us more about the side effects you are experiencing',
    field: 'pcosFollowup_sideEffectsDetails',
    type: 'info',
    answer: {
      name: 'pcosFollowup_sideEffectsDetails',
      type: 'text',
      rows: 4,
      placeholder: 'Enter details'
    }
  },
  {
    label:
      'Would you like to continue your current treatment, change the dose, or change the medication?',
    field: 'pcosFollowup_treatmentPreference',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          key: 'continue_current',
          value: "I'm doing well and I would like to continue what I'm on"
        },
        {
          key: 'increase_dose',
          value:
            'I like the medication but I would like to increase the dose if there is a higher dose'
        },
        {
          key: 'decrease_dose',
          value:
            'I like the medication but I would like to decrease the dose if there is a lower dose'
        },
        {
          key: 'change_medication',
          value:
            'I would like to change the medication if there are other options'
        },
        {
          key: 'provider_discussion',
          value: "I'm not sure, have the provider reach out to discuss further"
        }
      ]
    }
  },
  {
    label: 'What other information or questions do you have for the doctor?',
    field: 'pcosFollowup_otherInformation',
    type: 'info',
    answer: {
      name: 'pcosFollowup_otherInformation',
      type: 'text',
      rows: 4,
      placeholder: 'Optional details or questions'
    }
  },
  {
    label:
      'Consent (Progesterone Therapy and Compounded GLP-1 / GIP-GLP-1 Receptor Agonist Therapy)',
    field: 'pcosFollowup_medicationConsent',
    type: 'select',
    educationalSlide: `You are requesting evaluation and possible treatment with progesterone therapy and/or a compounded GLP-1 receptor agonist or GIP/GLP-1 receptor agonist medication, such as semaglutide or tirzepatide. These therapies may be used to support hormonal balance, metabolic health, and weight management depending on your clinical evaluation and medical history.

Progesterone Therapy
Progesterone is a naturally occurring hormone involved in regulating the menstrual cycle and supporting hormonal balance. Progesterone therapy may be used in certain individuals to help regulate menstrual cycles, support endometrial health, address symptoms related to hormone imbalance, or provide other hormone-related benefits as determined by your healthcare provider.

Potential benefits may include improved menstrual cycle regularity, hormonal balance, and symptom relief related to progesterone deficiency. However, progesterone therapy may cause side effects. Possible side effects may include fatigue, dizziness, breast tenderness, headaches, mood changes, bloating, or changes in menstrual bleeding patterns. Less commonly, hormone therapy may be associated with blood clots, allergic reactions, or other adverse events. Your provider will evaluate your medical history to determine if progesterone therapy is appropriate for you.

Progesterone therapy is not intended to treat infertility through this program. However, treatment that improves hormonal balance may restore ovulation in some individuals, which may increase the chance of pregnancy.

Compounded GLP-1 and GIP/GLP-1 Receptor Agonist Therapy
GLP-1 receptor agonists and GIP/GLP-1 receptor agonists are medications that mimic incretin hormones involved in regulating appetite, slowing gastric emptying, promoting fullness, and improving metabolic health.

Compounded medications are custom-prepared by licensed pharmacists. These formulations may allow for alternative dosing schedules, titration strategies, dosing frequencies, routes of administration, or the addition of ingredients intended to improve tolerability or reduce side effects. Compounded medications are not reviewed or approved by the U.S. Food and Drug Administration (FDA) and are not subject to the same testing, clinical trials, or manufacturing oversight as FDA-approved brand-name products. As a result, compounded medications may differ from FDA-approved products in consistency, potency, efficacy, and safety.

GLP-1 receptor agonists and GIP/GLP-1 receptor agonists are typically prescribed for individuals who are obese, or for individuals who are overweight with related medical conditions. Use of these medications in patients for PCOS is considered off-label, meaning the medication is prescribed in a manner not specifically approved by the FDA. Off-label prescribing is a common and accepted medical practice when clinically appropriate and based on medical judgment.

Potential benefits of treatment may include weight loss or weight management, improved blood glucose control, reduced cardiovascular risk, and overall improvement in metabolic health.

Common side effects may include:
- Nausea
- Vomiting
- Diarrhea
- Constipation
- Decreased appetite
- Indigestion

Serious or less common risks may include pancreatitis, low blood sugar, gallbladder disease, kidney injury or worsening kidney function, allergic reactions, and gastrointestinal motility disorders such as gastroparesis. In rare cases, serious complications including hospitalization or death may occur.

Pregnancy and Fertility Considerations
Some treatments in this program, including hormone therapy and metabolic therapies, may improve hormonal balance and restore ovulation in certain patients, which may increase the chance of pregnancy even in individuals who previously had irregular ovulation.

This program does not provide treatment for fertility purposes.

GLP-1 and GIP/GLP-1 receptor agonist medications should not be used during pregnancy or while breastfeeding, as they may pose risks to a developing baby.

These medications may also reduce the effectiveness of oral contraceptives. If you use oral birth control, you should continue it with a barrier method such as condoms for the first month after starting treatment and for one month after any dose increase. You may choose to switch to a non-oral contraceptive method such as an intrauterine device or implant prior to starting treatment.

After stopping GLP-1 medications, effective contraception should be continued for at least two months before attempting to conceive to ensure the medication has cleared from your system.

Monitoring and Follow-Up
Ongoing follow-up may be required to monitor your response to treatment and assess for potential side effects. Medication doses may be adjusted based on tolerability and clinical response.

Patient Attestation
Please attest to the following confirming that all information you have provided is true and complete.

I verify that I am the patient completing this intake form. I confirm that I have reviewed and understood the questions asked of me and that the information I have provided is accurate and complete to the best of my knowledge. I understand that it is critical to share complete and accurate health information with my healthcare provider. I will not hold the provider or affiliated medical practice responsible for any oversights or omissions, whether intentional or unintentional, in the information I provided.

By proceeding with treatment, you acknowledge that compounded medications are not FDA-approved, that they may differ from FDA-approved products in safety and effectiveness, and that you understand the potential benefits, risks, side effects, pregnancy-related precautions, and possible off-label use associated with progesterone therapy and GLP-1 or GIP/GLP-1 receptor agonist therapy. You agree to follow prescribed instructions, participate in recommended follow-up, and promptly report any concerning symptoms or side effects.
`,
    answer: {
      type: 'single_select',
      options: [
        {
          key: 'consent',
          value:
            'I have read the above information and I do consent and wish to move forward'
        },
        {
          key: 'non_consent',
          value:
            'I have read the above information and I do not wish to continue'
        }
      ]
    }
  },
  {
    field: 'pcosFollowup_complete',
    isLast: true,
    label:
      'Thank you for completing this questionnaire, please select "Finish" to submit',
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    field: 'pcosFollowup_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    type: 'info',
    isLast: true,
    blockQuestionFlow: true,
    answer: {},
    extras: { sendToBeluga: false }
  }
] as const;

const _type = pcosFollowupQuestions;
export type PCOSFollowupQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof _type>
>;

export default pcosFollowupQuestions;
